import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ToastNotify } from '@/types/toast';

/**
 * UI Slice - Manages global UI state
 * - Toast notifications (queue system)
 * - Loading states
 * - Error messages
 */

interface UIState {
  // Toast state
  toasts: ToastNotify[];

  // Global loading state
  isLoading: boolean;
  loadingMessage: string | null;

  // Global error state
  error: string | null;
  errorDetails: Record<string, unknown> | null;
}

const initialState: UIState = {
  toasts: [],
  isLoading: false,
  loadingMessage: null,
  error: null,
  errorDetails: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Toast actions
    addToast: (state, action: PayloadAction<Omit<ToastNotify, 'id'>>) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      state.toasts.push({ ...action.payload, id });
    },

    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload);
    },

    clearAllToasts: (state) => {
      state.toasts = [];
    },

    // Loading actions
    setLoading: (state, action: PayloadAction<{ isLoading: boolean; message?: string }>) => {
      state.isLoading = action.payload.isLoading;
      state.loadingMessage = action.payload.message || null;
    },

    // Error actions
    setError: (state, action: PayloadAction<{ error: string | null; details?: Record<string, unknown> }>) => {
      state.error = action.payload.error;
      state.errorDetails = action.payload.details || null;
    },

    clearError: (state) => {
      state.error = null;
      state.errorDetails = null;
    },
  },
});

export const {
  addToast,
  removeToast,
  clearAllToasts,
  setLoading,
  setError,
  clearError,
} = uiSlice.actions;

export default uiSlice.reducer;
